import React, { useEffect, useMemo, useRef } from "react";
import { EditorState, Compartment } from "@codemirror/state";
import { EditorView, keymap, placeholder as cmPlaceholder, lineNumbers, highlightActiveLine, MatchDecorator, ViewPlugin, Decoration, type DecorationSet, type ViewUpdate, drawSelection } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { HighlightStyle, syntaxHighlighting, bracketMatching, indentOnInput } from "@codemirror/language";
import { markdown } from "@codemirror/lang-markdown";
import { json } from "@codemirror/lang-json";
import { tags as t } from "@lezer/highlight";
import { diffLines } from "diff";
import { cx } from "./ui";

const varMatcher = new MatchDecorator({ regexp: /\{\{\s*[a-zA-Z_][\w-]*\s*\}\}/g, decoration: Decoration.mark({ class: "var-token" }) });
const varHighlight = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = varMatcher.createDeco(view);
    }
    update(u: ViewUpdate) {
      this.decorations = varMatcher.updateDeco(u, this.decorations);
    }
  },
  { decorations: (v) => v.decorations }
);

const theme = EditorView.theme(
  {
    "&": { backgroundColor: "transparent", color: "var(--text-1)", fontSize: "12.5px" },
    ".cm-content": { fontFamily: "var(--mono)", padding: "10px 0", caretColor: "var(--text-1)", lineHeight: "1.65" },
    ".cm-line": { padding: "0 12px" },
    ".cm-gutters": { backgroundColor: "transparent", border: "none", color: "var(--text-4)", fontFamily: "var(--mono)", fontSize: "11px" },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 6px 0 12px", minWidth: "34px" },
    ".cm-activeLine": { backgroundColor: "transparent" },
    "&.cm-focused .cm-activeLine": { backgroundColor: "hsl(0 0% 100% / 0.025)" },
    ".cm-activeLineGutter": { backgroundColor: "transparent", color: "var(--text-2)" },
    ".cm-cursor": { borderLeftColor: "var(--text-1)", borderLeftWidth: "1.5px" },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection": { backgroundColor: "hsl(258 88% 74% / 0.22) !important" },
    ".cm-placeholder": { color: "var(--text-4)" },
    ".cm-scroller": { overflow: "auto" },
    "&.cm-focused": { outline: "none" },
  },
  { dark: true }
);

const highlight = HighlightStyle.define([
  { tag: t.heading, color: "var(--text-1)", fontWeight: "600" },
  { tag: [t.heading1, t.heading2], color: "var(--text-1)", fontWeight: "650" },
  { tag: t.strong, fontWeight: "650", color: "var(--text-1)" },
  { tag: t.emphasis, fontStyle: "italic" },
  { tag: [t.monospace], color: "hsl(38 85% 70%)" },
  { tag: [t.list, t.quote], color: "var(--text-2)" },
  { tag: [t.link, t.url], color: "var(--blue)" },
  { tag: t.propertyName, color: "var(--g10)" },
  { tag: t.string, color: "hsl(152 45% 64%)" },
  { tag: [t.number], color: "hsl(38 85% 66%)" },
  { tag: [t.bool, t.null], color: "var(--violet)" },
  { tag: [t.punctuation, t.bracket, t.processingInstruction, t.meta], color: "var(--text-3)" },
]);

export function CodeEditor({
  value, onChange, language = "markdown", readOnly, minHeight = 80, maxHeight, placeholder, numbers, onSubmit, className, prose,
}: {
  value: string; onChange?: (v: string) => void; language?: "markdown" | "json" | "text"; readOnly?: boolean; minHeight?: number; maxHeight?: number;
  placeholder?: string; numbers?: boolean; onSubmit?: () => void; className?: string; prose?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const ro = useMemo(() => new Compartment(), []);
  const cbs = useRef({ onChange, onSubmit });
  cbs.current = { onChange, onSubmit };

  useEffect(() => {
    const lang = language === "json" ? [json()] : language === "markdown" ? [markdown()] : [];
    const state = EditorState.create({
      doc: value,
      extensions: [
        numbers ? lineNumbers() : [],
        history(),
        drawSelection(),
        indentOnInput(),
        bracketMatching(),
        highlightActiveLine(),
        keymap.of([
          { key: "Mod-Enter", run: () => (cbs.current.onSubmit?.(), true) },
          indentWithTab,
          ...defaultKeymap,
          ...historyKeymap,
        ]),
        EditorView.lineWrapping,
        theme,
        syntaxHighlighting(highlight),
        varHighlight,
        ...lang,
        placeholder ? cmPlaceholder(placeholder) : [],
        ro.of([EditorState.readOnly.of(!!readOnly), EditorView.editable.of(!readOnly)]),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) cbs.current.onChange?.(u.state.doc.toString());
        }),
      ],
    });
    view.current = new EditorView({ state, parent: host.current! });
    return () => view.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, numbers]);

  useEffect(() => {
    const v = view.current;
    if (v && v.state.doc.toString() !== value) v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: value } });
  }, [value]);

  useEffect(() => {
    view.current?.dispatch({ effects: ro.reconfigure([EditorState.readOnly.of(!!readOnly), EditorView.editable.of(!readOnly)]) });
  }, [readOnly, ro]);

  return (
    <div className={cx("editor-box", readOnly && "readonly", prose && "prose", className)}>
      <div ref={host} style={{ minHeight, maxHeight, overflow: maxHeight ? "auto" : undefined }} />
    </div>
  );
}

// ---------- diff ----------
export function DiffView({ title, a, b, context = 3 }: { title?: React.ReactNode; a: string; b: string; context?: number }) {
  const rows = useMemo(() => {
    const parts = diffLines(a, b);
    const out: { kind: "add" | "del" | "same" | "gap"; text: string; ln?: number }[] = [];
    let lnA = 1;
    let lnB = 1;
    parts.forEach((p, idx) => {
      const lines = p.value.replace(/\n$/, "").split("\n");
      if (p.added) lines.forEach((l) => out.push({ kind: "add", text: l, ln: lnB++ }));
      else if (p.removed) lines.forEach((l) => out.push({ kind: "del", text: l, ln: lnA++ }));
      else {
        const first = idx === 0;
        const last = idx === parts.length - 1;
        if (lines.length > context * 2 + 1) {
          const head = first ? [] : lines.slice(0, context);
          const tail = last ? [] : lines.slice(-context);
          head.forEach((l) => (out.push({ kind: "same", text: l, ln: lnB }), lnA++, lnB++));
          const skipped = lines.length - head.length - tail.length;
          out.push({ kind: "gap", text: `⋯ ${skipped} linhas iguais` });
          lnA += skipped;
          lnB += skipped;
          tail.forEach((l) => (out.push({ kind: "same", text: l, ln: lnB }), lnA++, lnB++));
        } else lines.forEach((l) => (out.push({ kind: "same", text: l, ln: lnB }), lnA++, lnB++));
      }
    });
    return out;
  }, [a, b, context]);
  const changed = rows.some((r) => r.kind === "add" || r.kind === "del");
  return (
    <div className="diff">
      {title && (
        <div className="diff-file">
          {title}
          {!changed && <span className="faint" style={{ marginLeft: "auto" }}>sem mudanças</span>}
        </div>
      )}
      {changed &&
        rows.map((r, i) =>
          r.kind === "gap" ? (
            <div key={i} className="diff-gap">{r.text}</div>
          ) : (
            <div key={i} className={cx("diff-line", r.kind)}>
              <span className="ln">{r.ln}</span>
              <span className="sg">{r.kind === "add" ? "+" : r.kind === "del" ? "−" : ""}</span>
              <span className="tx">{r.text || " "}</span>
            </div>
          )
        )}
    </div>
  );
}
